import { useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useOktaAuth } from '@okta/okta-react';
import HelpIcon from '@mui/icons-material/Help';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
  Stack,
} from '@mui/material';
import { HelpOutlined } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { LockOrderContext } from '../../context/LockOrderContext';
import { postHelpRequest } from '../../providers/helpApi';
import { MultilineTextInput, TextInput } from '.././inputs/TextInput';
import { SafeKeyButton, ButtonColors } from './SafeKeyButton';
import HelpRequest from '../../models/HelpRequest';
import { useLogger } from '../../hooks/useLogger';

const confluenceUrl: string | undefined = window.REACT_APP_CONFLUENCE;

const HelpButton = (): JSX.Element => {
  const logger = useLogger();
  const { ccid, sapOrderNumber, designID } = useContext(LockOrderContext);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [summary, setSummary] = useState('');
  const [description, setDescription] = useState('');
  const location = useLocation();

  const { oktaAuth, authState } = useOktaAuth();

  useEffect(() => {
    const getOktaUser = async () => {
      if (authState?.isAuthenticated) {
        const user = await oktaAuth.getUser();
        setUserEmail(user.email || '');
      }
    };

    getOktaUser();
  }, [authState?.isAuthenticated]);

  const toggleDialog = (): void => {
    setIsDialogOpen(!isDialogOpen);
    setSummary('');
    setDescription('');
  };

  /**
   *
   * @returns
   */
  const submitHelpRequest = async (): Promise<void> => {
    // required fields
    if (summary.trim() === '' || description.trim() === '') {
      return;
    }

    toast.info('Creating Jira ticket...');
    toggleDialog();
    try {
      var helpRequest = new HelpRequest(
        userEmail,
        summary,
        description,
        ccid,
        sapOrderNumber,
        designID.toString(),
        location.pathname
      );
      var helpResponse = await postHelpRequest(
        helpRequest,
        authState?.accessToken?.accessToken || ''
      );
      toast.info(`Jira ticket ${helpResponse} created`);
    } catch (e) {
      logger.UI.error(`Error Creating Jira Ticket: `, e);
      toast.error(`Error Creating Jira Ticket`);
    }
  };

  const onFaqClick = () => {
    window.open(confluenceUrl, '_safekeyfaq');
  };

  return (
    <>
      <Dialog
        open={isDialogOpen}
        onClose={toggleDialog}
      >
        <DialogTitle>
          Request Assistance
          <Tooltip
            hidden={confluenceUrl == null}
            title='FAQ'
          >
            <HelpOutlined
              color='primary'
              style={{ cursor: 'pointer' }}
              onClick={onFaqClick}
            />
          </Tooltip>
        </DialogTitle>
        <DialogContent sx={{ px: '1em' }}>
          <Stack spacing={1}>
            <TextInput
              readOnly
              labelText={`User`}
              value={userEmail ?? ''}
            />
            <TextInput
              labelText={'Summary'}
              value={summary}
              onChange={(newValue) => setSummary(newValue)}
            />
            <MultilineTextInput
              labelText='Description'
              value={description}
              onChange={(newValue) => setDescription(newValue)}
              style={{ height: '230px', resize: 'vertical' }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <SafeKeyButton
            buttonText={`Cancel`}
            buttonColor={ButtonColors.White}
            onClick={toggleDialog}
          />
          <SafeKeyButton
            buttonText={`Submit`}
            onClick={submitHelpRequest}
            disabled={false}
          />
        </DialogActions>
      </Dialog>
      <span
        onClick={toggleDialog}
        style={
          !authState?.isAuthenticated
            ? { display: 'none' }
            : {
                position: 'fixed',
                bottom: '11px',
                right: '15px',
                cursor: 'pointer',
                zIndex: 100,
              }
        }
        title={`Ask for help`}
      >
        <HelpIcon
          color='primary'
          fontSize='large'
        />
      </span>
    </>
  );
};

export default HelpButton;
